import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { chatbotService } from "../../services/chatbotService";
import { flowService } from "../../services/flowService";
import { FlowSelect } from "../../components/flow-select/FlowSelect";
import { Chatbot, Flow } from "../../types";

const schema = z.object({
  system_prompt: z.string().min(10, "Minimo 10 caratteri"),
  model: z.enum(["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-terra"]),
  temperature: z.number().min(0).max(2),
  max_tokens: z
    .number({ invalid_type_error: "Numero richiesto" })
    .int()
    .min(1)
    .max(32000),
  max_history: z
    .number({ invalid_type_error: "Numero richiesto" })
    .int()
    .min(1)
    .max(50),
  flow_id: z.string().nullable(),
});

type FormData = z.infer<typeof schema>;

export default function ChatbotDetail() {
  const { chatbotId } = useParams<{ chatbotId: string }>();
  const navigate = useNavigate();
  const [chatbot, setChatbot] = useState<Chatbot | null>(null);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!chatbotId) return;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await chatbotService.getById(chatbotId);
        const c = res.data.data;
        setChatbot(c);
        reset({
          system_prompt: c.system_prompt,
          model: c.model,
          temperature: c.temperature,
          max_tokens: c.max_tokens,
          max_history: c.max_history,
          flow_id: c.flow_id,
        });
        // Load flows for this tenant
        const flowsRes = await flowService.list(c.tenant_id);
        setFlows(flowsRes.data.data.flows);
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento del chatbot");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [chatbotId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = handleSubmit(async (data) => {
    if (!chatbotId) return;
    setError(null);
    setSaveSuccess(false);
    try {
      await chatbotService.update(chatbotId, data);
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      setError("Errore durante il salvataggio");
    }
  });

  const handleRemoveFlow = async () => {
    if (!chatbotId) return;
    setError(null);
    setSaveSuccess(false);
    try {
      await chatbotService.update(chatbotId, { flow_id: null });
      reset((prev) => ({ ...prev, flow_id: null }));
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      setError("Errore durante la rimozione del flow");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chatbot) {
    return (
      <div className="container">
        <Alert severity="error">Chatbot non trovato</Alert>
      </div>
    );
  }

  return (
    <div className="container">
      <FormLabel label={`Chatbot: ${chatbot.id.slice(0, 8)}…`} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Modifiche salvate
        </Alert>
      )}

      <Box
        sx={{
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
          p: 3,
        }}
        className="flex-col gap-2"
      >
        <form onSubmit={onSubmit} className="flex-col gap-2">
          {/* System prompt */}
          <Controller
            name="system_prompt"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={5}
                size="small"
                label="System Prompt"
                error={!!errors.system_prompt}
                helperText={errors.system_prompt?.message}
              />
            )}
          />

          {/* Model */}
          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.model}>
                <InputLabel id="model-label">Modello</InputLabel>
                <Select {...field} labelId="model-label" label="Modello" value={field.value ?? ""}>
                  <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                  <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                  <MenuItem value="gpt-4-turbo">gpt-4-turbo</MenuItem>
                  <MenuItem value="gpt-terra">gpt-terra</MenuItem>
                </Select>
                {errors.model && (
                  <FormHelperText>{errors.model.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Temperature */}
          <Controller
            name="temperature"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Temperature: {field.value}
                </Typography>
                <Slider
                  value={field.value ?? 0.7}
                  onChange={(_, v) => field.onChange(v)}
                  min={0}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 1, label: "1" },
                    { value: 2, label: "2" },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            )}
          />

          <div className="flex-row gap-2">
            {/* Max tokens */}
            <Controller
              name="max_tokens"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Tokens"
                  inputProps={{ min: 1, max: 32000 }}
                  error={!!errors.max_tokens}
                  helperText={errors.max_tokens?.message}
                />
              )}
            />
            {/* Max history */}
            <Controller
              name="max_history"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  fullWidth
                  size="small"
                  type="number"
                  label="Max History (messaggi)"
                  inputProps={{ min: 1, max: 50 }}
                  error={!!errors.max_history}
                  helperText={errors.max_history?.message}
                />
              )}
            />
          </div>

          {/* Flow selector */}
          <Box>
            <FormLabel label="Flow associato" />
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mt: 1 }}>
              <Controller
                name="flow_id"
                control={control}
                render={({ field }) => (
                  <FlowSelect
                    value={field.value ?? null}
                    onChange={field.onChange}
                    flows={flows}
                  />
                )}
              />
              {chatbot.flow_id && (
                <Button
                  variant="outlined"
                  color="warning"
                  size="small"
                  onClick={handleRemoveFlow}
                  sx={{ whiteSpace: "nowrap", mt: 0.5 }}
                >
                  Rimuovi flow
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/chatbot/chatbots")}
              disabled={isSubmitting}
            >
              Indietro
            </Button>
            <Button variant="contained" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={20} /> : "Salva modifiche"}
            </Button>
          </Box>
        </form>
      </Box>
    </div>
  );
}
